import { Box, Checkbox, Flex, FormControl, FormErrorMessage, Radio, RadioGroup, Tooltip } from '@chakra-ui/react'
import type { PolicyHolderAdditionalData, WizardPropertyAdditionalData } from 'BrokerTsLib/src/dtos'
import { Insurer, Product, Selection } from 'brokertslib/src/enums'
import { Controller, useFormContext } from 'BrokerTsLib/src/rhf'
import { useContext, useMemo } from 'react'

import { useApp } from '@/hooks/useApp'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { FormGroup } from '../components/FormGroup'
import { FormLabel } from '../components/FormLabel'
import { FormSpacer } from '../components/FormSpacer'
import { Panel } from '../components/Panel'
import { RadioStack } from '../components/RadioStack'
import { GetFormItemName, useNameof } from '../hooks/useNameof'
import { Info } from '../icons'
import { insuranceParamsContext } from '../layouts/Wizard'
import { Step4Context } from '../pages/chaloupka/wizard/4'
import { BCNOptions } from './BCNOptions'
import { ICOOptions } from './ICOOptions'
import { InsureeAddressRuian } from './InsureeAddressRuian'
import InsureeContact from './InsureeContact'
import { InsureeName } from './InsureeName'
import { InsureeType } from './InsureeType'

export const AdditionalInsureePropertyInfo = () => {
  const { product } = useApp()
  const { insuranceParams } = useContext(insuranceParamsContext)
  const { offer } = useContext(Step4Context)

  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const { register, watch, control, formState: { errors }, getFieldState, setValue, getValues } = useFormContext<WizardPropertyAdditionalData>()
  const nameof = useNameof<WizardPropertyAdditionalData>()
  const phnameof = useNameof<PolicyHolderAdditionalData>(nameof(x => x.policyHolder))

  const insuredPersonSameAs = watch('policyHolder.insuredPersonSameAs')
  const contactSameAs = watch('policyHolder.contactSameAs')

  const sameAsOptions = useMemo(() => [
    { label: 'Stejný s pojistníkem', value: Selection.PersonSameAsPolicyHolder },
    { label: 'Stejný s pojištěným', value: Selection.PersonSameAsClient },
    { label: 'Jiný', value: null },
  ].filter(x => insuredPersonSameAs != Selection.PersonSameAsPolicyHolder || x.value != Selection.PersonSameAsClient), [])

  const { error: agreementError } = getFieldState('policyHolder.person.agreement')
  const { error: agreementOtherError } = getFieldState('policyHolder.person.agreementOther')

  const phType = watch('policyHolder.person.type')
  const insuredPersonType = watch('policyHolder.insuredPerson.type')
  const contactType = watch('policyHolder.contact.type')

  const isLiability = product == Product.LiabilityInsurance

  return (
    <>
      {/* policy holder */}
      <>
        <InsureeType personPrefix={GetFormItemName<WizardPropertyAdditionalData>(x => x.policyHolder.person)} disabled />

        <FormSpacer />

        {isIn(phType, Selection.Person, Selection.SelfEmployed) &&
          <>
            <BCNOptions personPrefix={GetFormItemName<WizardPropertyAdditionalData>(x => x.policyHolder.person)} disabled />
            <FormGroup>
              <InsureeName personPrefix={GetFormItemName<WizardPropertyAdditionalData>(x => x.policyHolder.person)} isDisabled showTitles />
            </FormGroup>
          </>
        }

        {isIn(phType, Selection.Company, Selection.SelfEmployed) && <ICOOptions personPrefix={GetFormItemName<WizardPropertyAdditionalData>(x => x.policyHolder.person)} disabled showAgent={getValues('policyHolder.person.agent.required') ?? false} />}

        <FormSpacer />

        <InsureeAddressRuian addressPrefix={GetFormItemName<WizardPropertyAdditionalData>(x => x.policyHolder!.person!.address)} isDisabled={isContracted || isLiability} />

        <InsureeContact personPrefix={phnameof(x => x.person)} isDisabled={isContracted} />

        <FormControl isInvalid={!!agreementError?.message}>
          <Flex sx={{ gap: 2 }}>
            <Checkbox {...register('policyHolder.person.agreement')} isDisabled={isContracted}>
              {offer.insurerId == Insurer.GeneraliCeskaPojistovna && 'Souhlas s el. komunikací při jednání o uzavření pojistné smlouvy'}
              {offer.insurerId != Insurer.GeneraliCeskaPojistovna && 'Souhlas s elektronickou komunikací'}
            </Checkbox>
            <Tooltip
              shouldWrapChildren
              hasArrow
              label="Klient souhlasí se zasíláním veškeré komunikace (např. upozornění na splatnost) prostřednictvím zadaného e-mailu, příp. mobilního telefonu."
              placement="top"
            >
              <Info color="blue.500" />
            </Tooltip>
          </Flex>
          {<FormErrorMessage>{agreementError?.message}</FormErrorMessage>}
        </FormControl>

        {offer.insurerId == Insurer.GeneraliCeskaPojistovna &&
          <FormControl isInvalid={!!agreementOtherError?.message}>
            <Flex sx={{ gap: 2 }}>
              <Checkbox {...register('policyHolder.person.agreementOther')} isDisabled={isContracted}>
                Souhlas s el. komunikací během trvání pojištění
              </Checkbox>
              <Tooltip
                shouldWrapChildren
                hasArrow
                label={<Box dangerouslySetInnerHTML={{ __html: 'Klient souhlasí a požaduje odesílání informací a DOKUMENTŮ BĚHEM TRVÁNÍ POJIŠTĚNÍ A PŘI ZMĚNĚ PS elektronicky (e-mailem příp. SMS).<br/><br/>Při nesouhlasu budou DOKUMENTY BĚHEM TRVÁNÍ POJIŠTĚNÍ A PŘI ZMĚNĚ PS zaslány v listinné podobě.<br/><br/>POZOR: nesouhlas ovlivňuje všechny klientovy PS (aktivní i budoucí). V případě nesouhlasu, NEBUDOU klientovi odcházet e-maily ani SMS na žádnou z aktivních PS v NŽP i ŽP.<br/><br/>DOKUMENTY BĚHEM TRVÁNÍ POJIŠTĚNÍ jsou například Upozornění na splatnost, Výroční dopis, Upozornění na chybějící platbu A PŘI ZMĚNĚ PS například Informace o pojišťovně, produktu, nebo zprostředkovateli, Záznam z jednání , Pojistnou smlouvu a další dokumenty.' }}></Box>}
                placement="top"
              >
                <Info color="blue.500" />
              </Tooltip>
            </Flex>
            {<FormErrorMessage>{agreementOtherError?.message}</FormErrorMessage>}
          </FormControl>
        }
      </>

      <FormSpacer />

      {/* Insured */}
      <>
        <FormControl>
          <FormLabel>Pojištěný</FormLabel>

          <Controller
            name='policyHolder.insuredPersonSameAs'
            control={control}
            render={({ field: { onChange, value } }) => (
              <RadioGroup onChange={e => onChange(!e ? e : parseInt(e))} value={value?.toString() ?? ''} isDisabled>
                <RadioStack variant="stackedTile">
                  {
                    sameAsOptions
                      .filter(item => isIn(item.value, Selection.PersonSameAsPolicyHolder, null))
                      .map(item => (
                        <Radio key={item.value} value={item.value?.toString() ?? ''}>
                          {item.label}
                        </Radio>
                      ))
                  }
                </RadioStack>
              </RadioGroup>
            )}
          />
        </FormControl>

        {
          !insuredPersonSameAs &&
          <Panel>
            <InsureeType personPrefix={phnameof(x => x.insuredPerson)} disabled />

            {isIn(insuredPersonType, Selection.Person, Selection.SelfEmployed) && <BCNOptions personPrefix={phnameof(x => x.insuredPerson)} disabled />}
            {isIn(insuredPersonType, Selection.Company, Selection.SelfEmployed) && <ICOOptions personPrefix={phnameof(x => x.insuredPerson)} disabled showAgent={getValues('policyHolder.insuredPerson.agent.required') ?? false} />}
            {isIn(insuredPersonType, Selection.Person, Selection.SelfEmployed) &&
              <FormGroup><InsureeName personPrefix={phnameof(x => x.insuredPerson)} isDisabled showTitles /></FormGroup>
            }

            <InsureeAddressRuian addressPrefix={phnameof(x => x.insuredPerson!.address)} isDisabled={isContracted || isLiability} />

            <InsureeContact personPrefix={phnameof(x => x.insuredPerson)} isDisabled={isContracted} />

            <FormControl isInvalid={!!agreementError?.message}>
              <Flex sx={{ gap: 2 }}>
                <Checkbox {...register('policyHolder.insuredPerson.agreement')} isDisabled={isContracted}>
                  {offer.insurerId == Insurer.GeneraliCeskaPojistovna && 'Souhlas s el. komunikací při jednání o uzavření pojistné smlouvy'}
                  {offer.insurerId != Insurer.GeneraliCeskaPojistovna && 'Souhlas s elektronickou komunikací'}
                </Checkbox>
                <Tooltip
                  shouldWrapChildren
                  hasArrow
                  label="Klient souhlasí se zasíláním veškeré komunikace (např. upozornění na splatnost) prostřednictvím zadaného e-mailu, příp. mobilního telefonu."
                  placement="top"
                >
                  <Info color="blue.500" />
                </Tooltip>
              </Flex>
              {<FormErrorMessage>{agreementError?.message}</FormErrorMessage>}
            </FormControl>

            {offer.insurerId == Insurer.GeneraliCeskaPojistovna &&
              <FormControl isInvalid={!!agreementOtherError?.message}>
                <Flex sx={{ gap: 2 }}>
                  <Checkbox {...register('policyHolder.insuredPerson.agreementOther')} isDisabled={isContracted}>
                    Souhlas s el. komunikací během trvání pojištění
                  </Checkbox>
                  <Tooltip
                    shouldWrapChildren
                    hasArrow
                    label={<Box dangerouslySetInnerHTML={{ __html: 'Klient souhlasí a požaduje odesílání informací a DOKUMENTŮ BĚHEM TRVÁNÍ POJIŠTĚNÍ A PŘI ZMĚNĚ PS elektronicky (e-mailem příp. SMS).<br/><br/>Při nesouhlasu budou DOKUMENTY BĚHEM TRVÁNÍ POJIŠTĚNÍ A PŘI ZMĚNĚ PS zaslány v listinné podobě.<br/><br/>POZOR: nesouhlas ovlivňuje všechny klientovy PS (aktivní i budoucí). V případě nesouhlasu, NEBUDOU klientovi odcházet e-maily ani SMS na žádnou z aktivních PS v NŽP i ŽP.<br/><br/>DOKUMENTY BĚHEM TRVÁNÍ POJIŠTĚNÍ jsou například Upozornění na splatnost, Výroční dopis, Upozornění na chybějící platbu A PŘI ZMĚNĚ PS například Informace o pojišťovně, produktu, nebo zprostředkovateli, Záznam z jednání , Pojistnou smlouvu a další dokumenty.' }}></Box>}
                    placement="top"
                  >
                    <Info color="blue.500" />
                  </Tooltip>
                </Flex>
                {<FormErrorMessage>{agreementOtherError?.message}</FormErrorMessage>}
              </FormControl>
            }
          </Panel>
        }
      </>

      <FormSpacer />

      {/* Contact */}
      <>
        <FormControl>
          <FormLabel>Korespondenční adresa</FormLabel>

          <Controller
            name='policyHolder.contactSameAs'
            control={control}
            render={({ field: { onChange, value } }) => (
              <RadioGroup onChange={e => onChange(!e ? e : parseInt(e))} value={value?.toString() ?? ''} isDisabled={isContracted}>
                <RadioStack variant="stackedTile">
                  {
                    sameAsOptions
                      .map(item => (
                        <Radio key={item.value} value={item.value?.toString() ?? ''}>
                          {item.label}
                        </Radio>
                      ))
                  }
                </RadioStack>
              </RadioGroup>
            )}
          />
        </FormControl>

        {
          !contactSameAs && <Panel>
            <InsureeType personPrefix={phnameof(x => x.contact)} />

            {isIn(contactType, Selection.Person, Selection.SelfEmployed) &&
              <>
                <FormGroup>
                  <InsureeName personPrefix={phnameof(x => x.contact)} isDisabled={isContracted} showTitles />
                </FormGroup>
              </>
            }

            {isIn(contactType, Selection.Company, Selection.SelfEmployed) &&
              <ICOOptions personPrefix={phnameof(x => x.contact)} showAgent={false} hideIc={true} />
            }

            <InsureeAddressRuian addressPrefix={phnameof(x => x.contact!.address)} isDisabled={isContracted} />

          </Panel>
        }
      </>
    </>
  )
}
